import api from "../../api_interceptor/api";

/**
 * Active sections for the Edit Topic section dropdown.
 */
export const getHelpManualSectionList = async () => {
  const response = await api.get("api/GetHelpManualSectionList", {
    withCredentials: true,
  });
  return response?.data ?? {};
};

/**
 * Maps GetHelpManualSectionList into HelpManualSelect options.
 */
export const mapSectionListOptions = (data) => {
  const rows = data?.data ?? data?.Data ?? [];
  return rows
    .map((section) => ({
      value: String(section.id ?? section.Id ?? ""),
      label: section.name ?? section.Name ?? "",
    }))
    .filter((option) => option.value && option.label);
};

/**
 * Loads Place after Section options (Start + active sections).
 * @param {number|null} excludeSectionId
 */
export const getHelpManualSectionDropdown = async (excludeSectionId = null) => {
  const response = await api.post(
    "api/GetHelpManualSectionDropdown",
    { excludeSectionId: excludeSectionId || null },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    }
  );
  return response?.data ?? {};
};

/**
 * Creates or updates a help manual section.
 * @param {{ id?: number, name: string, placeAfterSectionId: number }} payload
 */
export const addOrEditHelpManualSection = async ({
  id = 0,
  name,
  placeAfterSectionId,
}) => {
  const response = await api.post(
    "api/AddOrEditHelpManualSection",
    {
      id: Number(id) || 0,
      name: String(name ?? "").trim(),
      placeAfterSectionId: Number(placeAfterSectionId),
    },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    }
  );
  return response?.data ?? {};
};

/**
 * Maps dropdown API payload to select options used by HelpManualSelect.
 */
export const mapSectionDropdownOptions = (data) => {
  const start = data?.startOption ?? data?.StartOption;
  const sections = data?.sections ?? data?.Sections ?? [];

  const startId = start?.id ?? start?.Id ?? 0;
  const startName = start?.name ?? start?.Name ?? "Start";

  const options = [
    { value: String(startId), label: startName },
    ...sections.map((s) => ({
      value: String(s.id ?? s.Id),
      label: s.name ?? s.Name ?? "",
    })),
  ];

  return options.filter((o) => o.label);
};

/**
 * Loads full Table of Contents: active sections with nested topics.
 */
export const getSectionAndTopicMaster = async () => {
  const response = await api.get("api/GetSectionAndTopicMaster", {
    withCredentials: true,
  });
  return response?.data ?? {};
};

/**
 * Loads one topic for the right-side preview/edit panel.
 * @param {number} topicId
 */
export const getHelpManualTopicById = async (topicId) => {
  const response = await api.get(`api/GetHelpManualTopic/${Number(topicId)}`, {
    withCredentials: true,
  });
  return response?.data ?? {};
};

/**
 * Soft-deletes a section (and its topics on the backend).
 * @param {{ id: number, isConfirmed?: boolean }} payload
 */
export const deleteHelpManualSectionApi = async ({
  id,
  isConfirmed = true,
}) => {
  const response = await api.post(
    "api/DeleteHelpManualSection",
    { id: Number(id), isConfirmed: Boolean(isConfirmed) },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    }
  );
  return response?.data ?? {};
};

/**
 * Preview text + topic count before deleting a section.
 * @param {number} sectionId
 */
export const getHelpManualSectionDeletePreview = async (sectionId) => {
  const response = await api.post(
    "api/GetHelpManualSectionDeletePreview",
    { id: Number(sectionId) },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    }
  );
  return response?.data ?? {};
};

/**
 * Soft-deletes a single topic.
 * @param {number} topicId
 */
export const deleteHelpManualTopicApi = async (topicId) => {
  const response = await api.post(
    "api/DeleteHelpManualTopic",
    { id: Number(topicId) },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    }
  );
  return response?.data ?? {};
};

/**
 * Place-after Topic dropdown (Start + topics in the section).
 * @param {{ sectionId: number, excludeTopicId?: number|null }} params
 */
export const getHelpManualTopicDropdown = async ({
  sectionId,
  excludeTopicId = null,
}) => {
  const response = await api.post(
    "api/GetHelpManualTopicDropdown",
    {
      sectionId: Number(sectionId),
      excludeTopicId: excludeTopicId ? Number(excludeTopicId) : null,
    },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    }
  );
  return response?.data ?? {};
};

/**
 * Maps topic dropdown payload to HelpManualSelect options.
 */
export const mapTopicDropdownOptions = (data) => {
  const start = data?.startOption ?? data?.StartOption;
  const topics = data?.topics ?? data?.Topics ?? [];

  const startId = start?.id ?? start?.Id ?? 0;
  const startName = start?.name ?? start?.Name ?? "Start";

  const options = [
    { value: String(startId), label: startName },
    ...topics.map((t) => ({
      value: String(t.id ?? t.Id),
      label: t.name ?? t.Name ?? "",
    })),
  ];

  return options.filter((o) => o.label);
};

/**
 * Module + activity tree for topic mapping UI.
 * @param {number|null} clientTypeId 1 = Enterprise, 2 = Site, null = all
 */
export const getActivityAndModuleMaster = async (clientTypeId = null) => {
  const response = await api.post(
    "api/GetActivityAndModuleMaster",
    { clientTypeId: clientTypeId == null ? null : Number(clientTypeId) },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    }
  );
  return response?.data ?? {};
};

/**
 * Maps GetActivityAndModuleMaster into ModuleActivityMapping tree shape.
 */
export const mapActivityAndModuleMaster = (data) => {
  const rows = data?.data ?? data?.Data ?? [];
  return rows
    .map((module) => {
      const activities = module.activity ?? module.Activity ?? [];
      return {
        id: String(module.id ?? module.Id ?? ""),
        name: module.moduleName ?? module.ModuleName ?? "",
        code: module.code ?? module.Code ?? "",
        clientTypeId: module.clientTypeId ?? module.ClientTypeId ?? null,
        activities: activities
          .map((activity) => ({
            id: String(activity.id ?? activity.Id ?? ""),
            name: activity.name ?? activity.Name ?? "",
            code: activity.code ?? activity.Code ?? "",
          }))
          .filter((a) => a.id),
      };
    })
    .filter((m) => m.id && m.name);
};

/**
 * Creates a topic (multipart — optional video file).
 */
export const addHelpManualTopic = async ({
  sectionId,
  name,
  description,
  placeAfterTopicId = 0,
  activityIds = "",
  videoFile = null,
}) => {
  const formData = new FormData();
  formData.append("Id", "0");
  formData.append("SectionId", String(Number(sectionId)));
  formData.append("Name", String(name ?? "").trim());
  formData.append("Description", String(description ?? ""));
  formData.append("PlaceAfterTopicId", String(Number(placeAfterTopicId) || 0));
  formData.append("ActivityIds", String(activityIds ?? ""));
  if (videoFile instanceof File) {
    formData.append("video", videoFile);
  }

  const response = await api.post("api/AddHelpManualTopic", formData, {
    withCredentials: true,
  });
  return response?.data ?? {};
};

/**
 * Updates a topic (multipart — optional replacement video).
 */
export const updateHelpManualTopic = async ({
  id,
  sectionId,
  name,
  description,
  placeAfterTopicId = 0,
  activityIds = "",
  videoPath = null,
  videoFile = null,
}) => {
  const formData = new FormData();
  formData.append("Id", String(Number(id)));
  formData.append("SectionId", String(Number(sectionId)));
  formData.append("Name", String(name ?? "").trim());
  formData.append("Description", String(description ?? ""));
  formData.append("PlaceAfterTopicId", String(Number(placeAfterTopicId) || 0));
  formData.append("ActivityIds", String(activityIds ?? ""));
  if (videoPath) {
    formData.append("VideoPath", String(videoPath));
  }
  if (videoFile instanceof File) {
    formData.append("video", videoFile);
  }

  const response = await api.post("api/UpdateHelpManualTopic", formData, {
    withCredentials: true,
  });
  return response?.data ?? {};
};

/**
 * Moves a topic into another section. PlaceAfterTopicId 0 means Start.
 */
export const changeHelpManualTopicSection = async ({
  id,
  sectionId,
  placeAfterTopicId = 0,
}) => {
  const response = await api.post(
    "api/ChangeHelpManualTopicSection",
    {
      id: Number(id),
      sectionId: Number(sectionId),
      placeAfterTopicId: Number(placeAfterTopicId) || 0,
    },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    }
  );
  return response?.data ?? {};
};

const fileNameFromPath = (path) => {
  if (!path) return "";
  const cleaned = String(path).split("?")[0];
  const parts = cleaned.split(/[/\\]/);
  return parts[parts.length - 1] || cleaned;
};

/**
 * Maps GetSectionAndTopicMaster payload into UI section/topic tree.
 */
export const mapSectionAndTopicMaster = (data) => {
  const rows = data?.data ?? data?.Data ?? [];
  return rows
    .map((section) => {
      const topics = section.topic ?? section.Topic ?? [];
      return {
        id: section.id ?? section.Id,
        name: section.name ?? section.Name ?? "",
        sequence: section.sequence ?? section.Sequence ?? 0,
        topics: topics
          .map((topic) => {
            const videoPath = topic.videoPath ?? topic.VideoPath ?? null;
            return {
              id: topic.id ?? topic.Id,
              sectionId: topic.sectionId ?? topic.SectionId,
              name: topic.name ?? topic.Name ?? "",
              sequence: topic.sequence ?? topic.Sequence ?? 0,
              explanation: "",
              video: videoPath
                ? {
                    name: fileNameFromPath(videoPath),
                    sizeLabel: "",
                    ready: true,
                    url: videoPath,
                  }
                : null,
            };
          })
          .sort((a, b) => a.sequence - b.sequence),
      };
    })
    .filter((section) => section.id != null && section.name)
    .sort((a, b) => a.sequence - b.sequence);
};

/**
 * Resolves Place after Section value for edit form from ordered TOC sections.
 * @param {Array<{ id: number|string }>} sections
 * @param {number} sectionId
 * @returns {string} "0" for Start, otherwise previous section id
 */
export const resolvePlaceAfterSectionId = (sections, sectionId) => {
  const ordered = Array.isArray(sections) ? sections : [];
  const index = ordered.findIndex((s) => Number(s.id) === Number(sectionId));
  if (index <= 0) return "0";
  return String(ordered[index - 1].id);
};

/**
 * Searches sections, topics, and explanations (substring, case-insensitive).
 * @param {string} searchText
 */
export const searchHelpManual = async (searchText) => {
  const response = await api.post(
    "api/SearchHelpManual",
    { searchText: String(searchText ?? "").trim() },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    }
  );
  return response?.data ?? {};
};

/**
 * Maps SearchHelpManual payload into the same UI tree shape as TOC.
 */
export const mapHelpManualSearchResults = (data) => {
  const rows = data?.sections ?? data?.Sections ?? [];
  return rows
    .map((section) => {
      const topics = section.topics ?? section.Topics ?? [];
      return {
        id: section.id ?? section.Id,
        name: section.name ?? section.Name ?? "",
        sequence: section.sequence ?? section.Sequence ?? 0,
        sectionNameMatched: Boolean(
          section.sectionNameMatched ?? section.SectionNameMatched
        ),
        topics: topics
          .map((topic) => {
            const videoPath = topic.videoPath ?? topic.VideoPath ?? null;
            const description =
              topic.description ?? topic.Description ?? "";
            return {
              id: topic.id ?? topic.Id,
              sectionId: topic.sectionId ?? topic.SectionId,
              name: topic.name ?? topic.Name ?? "",
              sequence: topic.sequence ?? topic.Sequence ?? 0,
              explanation: description,
              video: videoPath
                ? {
                    name: fileNameFromPath(videoPath),
                    sizeLabel: "",
                    ready: true,
                    url: videoPath,
                  }
                : null,
            };
          })
          .sort((a, b) => a.sequence - b.sequence),
      };
    })
    .filter((section) => section.id != null && section.name)
    .sort((a, b) => a.sequence - b.sequence);
};
